import {ViteReactSSG} from "vite-react-ssg";
import {createBrowserRouter, matchRoutes} from "react-router-dom";
import scStyleCollector from "./seo/styledComponentsCollector";
// Self-hosted v3 fonts (Instrument Sans / Instrument Serif / JetBrains Mono) — bundled by Vite, so
// the SSG pre-render never fetches external font hosts.
import "./theme/fonts";
import "./index.css";
import {routes} from "./routes";
import {prepareClientOnlyRouteShell} from "./clientOnlyRouteShell";
import {installStaleChunkRecovery} from "./staleChunkRecovery";
import {installTranslatedDomGuard} from "./dom/translatedDomGuard";
import {completeStaticLoaderHydrationData, preserveRouteLoaders} from "./utils/routerHydrationData";

const routeLoaders = preserveRouteLoaders(routes);

type StaticRouterHydrationWindow = Window & {
	__staticRouterHydrationData?: {readonly loaderData?: Record<string, unknown>};
};

/**
 * SSG / SPA entry (replaces the old `index.tsx` createRoot bootstrap).
 *
 * `vite-react-ssg` drives this in two modes:
 *  - at build time it renders each public route to static HTML in Node (pre-rendering),
 *  - in the browser it hydrates and runs as the normal SPA.
 *
 * Global providers live in the root layout (`RootLayout`), so they are not repeated here. The
 * styled-components style collector ensures the styled-components 6 CSS is serialized into the
 * pre-rendered HTML.
 */
export const createRoot = ViteReactSSG(
	{
		routes: routeLoaders.routes,
		customCreateRouter: (generatedRoutes, options) => {
			const routeRecords = routeLoaders.restore(generatedRoutes);
			const hydrationWindow = window as StaticRouterHydrationWindow;
			const matchedRouteIds = matchRoutes(routeRecords, window.location, options?.basename)
				?.filter(({route}) => !route.loader).map(({route}) => route.id) ?? [];
			const hydrationData = completeStaticLoaderHydrationData(
				hydrationWindow.__staticRouterHydrationData,
				matchedRouteIds,
			);
			if (hydrationData !== undefined) {
				// StaticRouterProvider has already inlined this object into the document. Leave it on the
				// standard global so createBrowserRouter can perform its own route-error deserialization.
				hydrationWindow.__staticRouterHydrationData = hydrationData;
			}
			return createBrowserRouter(routeRecords, options);
		},
	},
	(context) => {
		// Excluded routes reach static hosting through the pre-rendered landing document. Neutralize that
		// fallback before vite-react-ssg chooses its render strategy, so these routes use createRoot rather
		// than hydrating unrelated landing HTML. The callback also runs during SSG, where browser globals stay
		// untouched; public route documents retain their marker and continue to hydrate normally.
		if (context.isClient) {
			// Before hydration, because the first re-render after a translation is what crashes: Chrome's
			// page translation re-parents the text nodes React owns, and the conditional re-render that
			// follows asks the old parent to remove a node that has moved (ui#2580).
			installTranslatedDomGuard();
			// A deploy can replace the hashed chunk names this tab still references; on the resulting
			// preload failure a one-shot reload swaps in fresh HTML instead of the error boundary (ui#1608).
			installStaleChunkRecovery(window, {
				reload: () => window.location.reload(),
				storage: () => window.sessionStorage,
			});
			prepareClientOnlyRouteShell(
				window.location.pathname,
				document.querySelector("#root"),
				document.documentElement,
			);
		}
		// The CRA-era boot-time `serviceWorker.unregister()` remains deliberately absent (ui#90): legacy
		// cleanup is owned by the ui#36 kill-switch at /service-worker.js.
	},
	{
		getStyleCollector: () => scStyleCollector(),
	},
);
